import { Store, UserRole } from '@nextforce/api'
import { Input, Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { AddEditUserQueryParams } from 'management_team/queries/shared/types'
import { StoreListItem } from 'management_team/store_chooser/views'
import { useRef } from 'react'
import { Pressable, View } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { SafeAreaView } from 'react-native-safe-area-context'
import { CheckBox } from 'shared/components/check_box'
import { RoundedButton } from 'shared/components/rounded-button'

interface Props {
  userFields: AddEditUserQueryParams
  setUserFields: React.Dispatch<React.SetStateAction<AddEditUserQueryParams>>
  stores: Store[] | undefined
  role: UserRole | undefined
  onPressChooseStores(): void
}

export function AddEditUserView({
  userFields,
  stores,
  role,
  setUserFields,
  onPressChooseStores,
}: Props) {
  const styles = useStyles()
  const scrollViewRef = useRef<KeyboardAwareScrollView | null>(null)

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <KeyboardAwareScrollView
        style={styles.container}
        ref={ref => {
          scrollViewRef.current = ref
        }}
        enableOnAndroid={true}
      >
        <View style={styles.card}>
          <Text style={styles.title}>{t('addUserInfoText')}</Text>
        </View>
        <Text style={styles.label}>{t('mobileNumber')}</Text>
        <View style={styles.inputContainer}>
          <Input
            style={styles.textInput}
            value={userFields.phoneNumber}
            maxLength={20}
            keyboardType="numeric"
            placeholder={t('mobileNumber')}
            onChangeText={text =>
              setUserFields({ ...userFields, phoneNumber: text })
            }
          />
        </View>
        <Text style={styles.label}>{t('firstName')}</Text>
        <View style={styles.inputContainer}>
          <Input
            style={styles.textInput}
            onChangeText={text => {
              setUserFields({ ...userFields, firstName: text })
            }}
            placeholder={t('firstName')}
            value={userFields.firstName}
          />
        </View>
        <Text style={styles.label}>{t('lastName')}</Text>
        <View style={styles.inputContainer}>
          <Input
            style={styles.textInput}
            onChangeText={text => {
              setUserFields({ ...userFields, lastName: text })
            }}
            placeholder={t('lastName')}
            value={userFields.lastName}
          />
        </View>
        {role === 'staff' || role === 'guest' ? (
          <>
            <Text style={styles.label}>{t('companyNameJobTitle')}</Text>
            <View style={styles.inputContainer}>
              <Input
                style={styles.textInput}
                onChangeText={text => {
                  setUserFields({ ...userFields, jobTitle: text })
                }}
                placeholder={t('companyNameJobTitle')}
                value={userFields.jobTitle}
              ></Input>
            </View>
          </>
        ) : null}
        {role === 'observer' ? (
          <>
            <Text style={styles.label}>{t('inviteToNext')}</Text>
            <Pressable
              onPress={() =>
                setUserFields({
                  ...userFields,
                  inviteToNext: !userFields.inviteToNext,
                })
              }
            >
              <View style={styles.inputContainer}>
                <Text>{t('inviteToNextDescription')}</Text>
                <CheckBox
                  checked={!!userFields.inviteToNext}
                  checkedColor={styles.error.color}
                  onPress={() =>
                    setUserFields({
                      ...userFields,
                      inviteToNext: !userFields.inviteToNext,
                    })
                  }
                />
              </View>
            </Pressable>
            <View style={styles.storesLabelContainer}>
              <Text style={styles.label}>{t('stores')}</Text>
              <RoundedButton
                title={t('choose')}
                onPress={onPressChooseStores}
                containerStyle={styles.chooseStoresButtonContainer}
              />
            </View>
            <AssociatedStoresList
              stores={stores}
              associatedStoreIds={userFields.storeIds}
            />
          </>
        ) : null}
      </KeyboardAwareScrollView>
    </SafeAreaView>
  )
}

function AssociatedStoresList({
  stores,
  associatedStoreIds,
}: {
  stores?: Store[]
  associatedStoreIds?: number[]
}) {
  const styles = useStyles()
  if (!associatedStoreIds?.length) {
    return (
      <View style={styles.inputContainer}>
        <Text>{t('atLeastOneStoreRequired')}</Text>
      </View>
    )
  }
  const StoreItems = stores
    ?.filter(store => associatedStoreIds?.includes(Number(store.id)))
    .map(store => <StoreListItem store={store} key={store.id} checked={true} />)
  if (StoreItems) {
    return <View>{StoreItems}</View>
  }
  return null
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
    backgroundColor: theme.colors.grey5,
  },
  inputContainer: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    paddingHorizontal: 10,
    paddingVertical: 15,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  textInput: {
    fontSize: 17,
    fontStyle: 'italic',
    color: theme.colors.grey1,
    paddingHorizontal: 10,
    width: '100%',
  },
  error: {
    margin: 10,
    textAlign: 'center',
    color: theme.colors.primary,
    fontFamily: font.bold,
    fontSize: 16,
  },
  card: {
    backgroundColor: theme.colors.white,
    padding: 30,
  },
  title: {
    textAlign: 'center',
    marginVertical: 10,
    fontSize: 18,
    color: theme.colors.grey1,
  },
  label: {
    margin: 12,
    fontSize: 16,
    color: theme.colors.grey1,
  },
  storesLabelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chooseStoresButtonContainer: { padding: 5 },
}))
